// =============================================================================
// SimSense Platform — Layout Shell
// =============================================================================
// Wraps all pages with the navigation bar.
// Includes auth guard — redirects to login if not authenticated.

import { useEffect, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { NavBar } from "./NavBar";
import { useAuth } from "../hooks/useAuth";
import { useUpdater } from "../hooks/useUpdater";
import { useTauri } from "../hooks/useTauri";
import { Login } from "../pages/Login";
import { OnboardingPrompt } from "../components/OnboardingPrompt";
import { WhatsNew } from "../components/WhatsNew";
import { remoteLog } from "../utils/logger";
import { useNavigate, useLocation, Navigate } from "react-router-dom";

type ShellProps = {
  children: ReactNode;
};

export function Shell({ children }: ShellProps) {
  const { isAuthenticated, loading, user } = useAuth();
  const { status: updateStatus, version: updateVersion, installUpdate } = useUpdater();
  const isTauri = useTauri();
  const navigate = useNavigate();
  const location = useLocation();
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Check if current path is a "guest" page (login, signup, landing)
  // "/" is guest only when not authenticated (shows landing for visitors, dashboard for users)
  const isGuestPage =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/pricing" ||
    (location.pathname === "/" && !isAuthenticated);

  // Guest pages (landing, login, signup, pricing) need document-level scrolling.
  // The global CSS sets overflow:hidden on html/body/#root for the app shell,
  // so we temporarily override it for guest pages.
  useEffect(() => {
    const root = document.getElementById("root");
    if (isGuestPage) {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "auto";
      if (root) root.style.overflow = "auto";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      if (root) root.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      if (root) root.style.overflow = "";
    };
  }, [isGuestPage]);

  useEffect(() => {
    void remoteLog("Shell rendered", "info", {
      isAuthenticated,
      loading,
      url: window.location.href,
      pathname: location.pathname
    });
  }, [isAuthenticated, loading, location.pathname]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    if (e.key === "Escape") {
      if (showShortcuts) {
        setShowShortcuts(false);
      } else {
        navigate(-1);
      }
    } else if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
      setShowShortcuts(prev => !prev);
    } else if (e.key === "h" && !e.ctrlKey && !e.metaKey) {
      navigate("/");
    }
  }, [navigate, showShortcuts]);

  useEffect(() => {
    if (isGuestPage) return; // No keyboard shortcuts on guest pages
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, isGuestPage]);

  // Case 1: Checking auth state
  if (loading) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        height: "100vh", width: "100%", background: "var(--bg1)", color: "var(--text2)",
        fontSize: "14px", gap: "16px"
      }}>
        <div style={{
          width: "32px", height: "32px", border: "3px solid var(--border)",
          borderTopColor: "var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        Connecting...
      </div>
    );
  }

  // Case 2: Not authenticated and not on a guest page -> Redirect to login
  if (!isAuthenticated && !isGuestPage) {
    return <Login />;
  }

  // Case 3: Not authenticated but ON a guest page -> Let the router handle it
  // (We return the children which contain the Routes, but Shell wraps it)
  // Wait, if I'm on /signup, I want to see SignUp.
  // The current structure has Shell wrapping all Routes in App.tsx.
  
  if (!isAuthenticated && isGuestPage) {
    return (
      <div style={{ minHeight: "100vh", width: "100%", overflow: "visible" }}>
        {children}
      </div>
    );
  }

  // Case 3b: Authenticated but on a login/signup page -> redirect to dashboard
  if (isAuthenticated && (location.pathname === "/login" || location.pathname === "/signup")) {
    return <Navigate to="/" replace />;
  }

  // Case 4: Authenticated -> Show full app with NavBar
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Desktop title bar — drag region with centered branding */}
      {isTauri && (
        <div
          data-tauri-drag-region=""
          style={{
            height: "48px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0a0e1a",
            borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
            position: "relative",
            zIndex: 60,
            userSelect: "none",
            // @ts-ignore
            WebkitAppRegion: "drag",
          }}
        >
          <div
            data-tauri-drag-region=""
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              pointerEvents: "none",
            }}
          >
            <div style={{
              width: "22px",
              height: "22px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              color: "#fff",
              fontSize: "12px",
              fontStyle: "italic",
            }}>
              S
            </div>
            <span style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.5)",
              letterSpacing: "0.3px",
            }}>
              SimSense
            </span>
          </div>
        </div>
      )}

      {/* Onboarding overlay for new users */}
      {user && !user.onboardingCompleted && <OnboardingPrompt />}

      {/* What's New popup */}
      {user && user.onboardingCompleted && <WhatsNew />}

      {/* Update banner (desktop only) */}
      {updateStatus === "available" && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
          padding: "8px 16px", background: "var(--accent-dim)", borderBottom: "1px solid var(--accent)",
          fontSize: "13px", flexShrink: 0,
        }}>
          <span style={{ color: "var(--text1)" }}>Update {updateVersion} is available.</span>
          <button onClick={installUpdate} style={{
            padding: "4px 12px", background: "var(--accent)", color: "#fff", border: "none",
            borderRadius: "var(--radius-sm)", fontSize: "12px", fontWeight: 600, cursor: "pointer",
          }}>Install & Restart</button>
        </div>
      )}

      {/* Top navigation */}
      <NavBar />

      {/* Page content */}
      <main style={{ flex: 1, overflow: "auto", padding: "24px" }}>
        {children}
      </main>

      {/* Keyboard Shortcuts Help */}
      {showShortcuts && (
        <div onClick={() => setShowShortcuts(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--card)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "24px 32px", maxWidth: "360px", width: "90%",
          }}>
            <h3 style={{ marginBottom: "16px", fontSize: "14px" }}>Keyboard Shortcuts</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <ShortcutRow keys="?" label="Show / hide this help" />
              <ShortcutRow keys="Esc" label="Go back" />
              <ShortcutRow keys="H" label="Go to Home" />
            </div>
            <div style={{ marginTop: "16px", fontSize: "11px", color: "var(--text2)" }}>
              Press Esc or click outside to close
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
      <span style={{ color: "var(--text2)" }}>{label}</span>
      <kbd style={{
        padding: "2px 8px", background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)",
        borderRadius: "4px", fontSize: "12px", fontFamily: "monospace", color: "var(--text1)", fontWeight: 500,
      }}>{keys}</kbd>
    </div>
  );
}
